/**
 * PublicTrackingPage
 * -------------------
 * Publicly-accessible page (no auth) rendered when someone opens the
 * share link `?/track/:token` sent via WhatsApp by a passenger. It shows
 * a live Google map with:
 *   - the vehicle's current position (auto-refreshes every 5s)
 *   - the trip route (departure -> destination)
 *   - the estimated time of arrival (ETA)
 *   - driver name, vehicle plate + color
 * Access is revoked automatically 24h after the trip ends (server-side).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { APIProvider, Map, Marker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Loader2, MapPin, Navigation, User, Car, AlertTriangle, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Reuse the same city coordinate table used elsewhere so a trip without
// explicit coordinates still renders a route between known city centres.
const CITY_COORDS = {
  "yaounde": { lat: 3.848, lng: 11.5021 }, "douala": { lat: 4.0511, lng: 9.7679 },
  "bafoussam": { lat: 5.4737, lng: 10.4176 }, "bamenda": { lat: 5.9597, lng: 10.1460 },
  "garoua": { lat: 9.3014, lng: 13.3987 }, "maroua": { lat: 10.5910, lng: 14.3158 },
  "ngaoundere": { lat: 7.3215, lng: 13.5844 }, "bertoua": { lat: 4.5773, lng: 13.6846 },
  "ebolowa": { lat: 2.9000, lng: 11.1500 }, "kribi": { lat: 2.9408, lng: 9.9098 },
  "limbe": { lat: 4.0219, lng: 9.2081 }, "buea": { lat: 4.1597, lng: 9.2295 },
};

function guessCityCoords(name) {
  if (!name) return null;
  const key = String(name).toLowerCase().trim();
  for (const [city, coords] of Object.entries(CITY_COORDS)) {
    if (key.includes(city) || city.includes(key)) return coords;
  }
  return null;
}

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Chauffeur en approche",
  in_progress: "Course en cours",
  completed: "Course terminée",
  cancelled: "Course annulée",
};

export default function PublicTrackingPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial metadata fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/track/public/${token}`);
        if (!cancelled) {
          setMeta(data);
          setLive(data.driver_location || null);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.status === 410
            ? "Ce lien de suivi a expiré. Demandez un nouveau lien au passager."
            : "Lien de suivi introuvable ou invalide.";
          setError(msg);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Poll driver location every 5s while trip is not finished
  useEffect(() => {
    if (!token || error) return;
    if (meta && (meta.status === "completed" || meta.status === "cancelled")) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/track/public/${token}/location`);
        if (data.lat != null && data.lng != null) {
          setLive({ lat: data.lat, lng: data.lng, updated_at: data.updated_at });
        }
        if (data.status && meta && data.status !== meta.status) {
          setMeta((m) => ({ ...m, status: data.status, otp_verified: !!data.otp_verified }));
        }
      } catch (_e) { /* silent — retry next tick */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [token, error, meta]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A2540] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FACC15]" />
      </div>
    );
  }

  if (error || !meta) {
    return (
      <div className="min-h-screen bg-[#0A2540] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center border border-[#CBD5E1]" data-testid="tracking-error">
          <AlertTriangle className="w-10 h-10 mx-auto text-[#EF4444] mb-3" />
          <h1 className="text-lg font-bold text-[#0A2540] mb-2" style={{ fontFamily: "Outfit" }}>Suivi indisponible</h1>
          <p className="text-sm text-[#64748B]">{error || "Impossible de charger ce trajet."}</p>
        </div>
      </div>
    );
  }

  return <TrackingView meta={meta} live={live} />;
}

function TrackingView({ meta, live }) {
  const departureCoords = useMemo(() => {
    if (meta.pickup_lat && meta.pickup_lng) return { lat: meta.pickup_lat, lng: meta.pickup_lng };
    return guessCityCoords(meta.departure) || guessCityCoords(meta.pickup_location);
  }, [meta.pickup_lat, meta.pickup_lng, meta.departure, meta.pickup_location]);
  const destinationCoords = useMemo(() => {
    if (meta.dropoff_lat && meta.dropoff_lng) return { lat: meta.dropoff_lat, lng: meta.dropoff_lng };
    return guessCityCoords(meta.destination) || guessCityCoords(meta.dropoff_location);
  }, [meta.dropoff_lat, meta.dropoff_lng, meta.destination, meta.dropoff_location]);
  const isFinished = meta.status === "completed" || meta.status === "cancelled";
  const statusLabel = STATUS_LABELS[meta.status] || meta.status || "Statut inconnu";

  const body = (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="public-tracking-page">
      {/* Header */}
      <header className="bg-[#0A2540] text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#FACC15] flex items-center justify-center">
              <Car className="w-4 h-4 text-[#0A2540]" />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>TrigoTour · Suivi en direct</p>
              <p className="text-[10px] text-white/60 flex items-center gap-1"><Shield className="w-2.5 h-2.5" /> Lien sécurisé partagé par {meta.passenger_name || "un passager"}</p>
            </div>
          </div>
          <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-full ${isFinished ? "bg-white/10 text-white/70" : "bg-[#FACC15] text-[#0A2540]"}`} data-testid="tracking-status-badge">
            {statusLabel}
          </span>
        </div>
      </header>

      {/* Map — force explicit height so Google Maps can compute tile size
          reliably. `flex-1` alone is unreliable inside a `min-h-screen` flex
          column because the child's height is not guaranteed to be bounded. */}
      <div className="relative w-full" style={{ height: "60vh", minHeight: "360px" }}>
        {MAPS_KEY ? (
          <TrackingMap
            departureCoords={departureCoords}
            destinationCoords={destinationCoords}
            driverPos={live}
            departure={meta.departure}
            destination={meta.destination}
          />
        ) : (
          <div className="h-full flex items-center justify-center bg-[#F8FAFC] text-sm text-[#64748B]">
            Carte non disponible (clé Google Maps manquante).
          </div>
        )}
      </div>

      {/* Info card */}
      <div className="bg-white border-t border-[#CBD5E1]">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-3">
          {/* Route */}
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center pt-1">
              <MapPin className="w-3.5 h-3.5 text-[#10B981]" />
              <div className="w-px h-6 bg-[#CBD5E1] my-0.5" />
              <MapPin className="w-3.5 h-3.5 text-[#EF4444]" />
            </div>
            <div className="flex-1 space-y-1.5">
              <p className="text-sm font-semibold text-[#0A2540]" data-testid="tracking-departure">{meta.pickup_address || meta.departure || meta.pickup_location || "Départ"}</p>
              <p className="text-sm font-semibold text-[#0A2540]" data-testid="tracking-destination">{meta.dropoff_address || meta.destination || meta.dropoff_location || "Destination"}</p>
            </div>
          </div>

          {/* Driver + vehicle */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-[#F1F5F9]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0A2540]/10 flex items-center justify-center">
                <User className="w-4 h-4 text-[#64748B]" />
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#64748B] font-semibold tracking-wide">Chauffeur</p>
                <p className="text-sm font-semibold text-[#0A2540]" data-testid="tracking-driver-name">{meta.driver_name || "Non assigné"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#FACC15]/25 flex items-center justify-center">
                <Car className="w-4 h-4 text-[#0A2540]" />
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#64748B] font-semibold tracking-wide">Véhicule</p>
                <p className="text-sm font-semibold text-[#0A2540]" data-testid="tracking-vehicle">
                  {[meta.vehicle_brand, meta.vehicle_model, meta.vehicle_color].filter(Boolean).join(" · ") || "N/A"}
                </p>
                {meta.plate_number && (
                  <p className="text-[10px] text-[#64748B]" data-testid="tracking-plate">{meta.plate_number}</p>
                )}
              </div>
            </div>
          </div>

          {/* ETA */}
          {!isFinished && (
            <EtaLine driverPos={live} destinationCoords={destinationCoords} />
          )}

          {isFinished && (
            <div className="mt-2 rounded-lg bg-[#F1F5F9] p-3 text-xs text-[#64748B]" data-testid="tracking-finished-note">
              La course est terminée. Ce lien reste consultable pendant 24h à titre d'historique.
            </div>
          )}
          <p className="text-[10px] text-[#64748B] text-center pt-2">Position mise à jour toutes les 5 secondes · <a className="underline" href="/">trigotour</a></p>
        </div>
      </div>
    </div>
  );

  if (!MAPS_KEY) return body;
  return <APIProvider apiKey={MAPS_KEY}>{body}</APIProvider>;
}

function EtaLine({ driverPos, destinationCoords }) {
  const routesLib = useMapsLibrary("routes");
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (!routesLib || !driverPos?.lat || !destinationCoords?.lat) return;
    const svc = new routesLib.DirectionsService();
    svc.route(
      {
        origin: { lat: driverPos.lat, lng: driverPos.lng },
        destination: { lat: destinationCoords.lat, lng: destinationCoords.lng },
        travelMode: routesLib.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result?.routes?.[0]?.legs?.[0]?.duration) {
          setEta(result.routes[0].legs[0].duration.text);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routesLib, driverPos?.lat, driverPos?.lng, destinationCoords?.lat, destinationCoords?.lng]);

  if (!driverPos?.lat) {
    return (
      <div className="mt-2 rounded-lg bg-[#F1F5F9] p-3 text-xs text-[#64748B] flex items-center gap-2" data-testid="tracking-eta-waiting">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> En attente de la position du véhicule…
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-lg bg-[#0A2540] p-3 text-white flex items-center justify-between" data-testid="tracking-eta">
      <div className="flex items-center gap-2">
        <Navigation className="w-4 h-4 text-[#FACC15]" />
        <span className="text-xs font-semibold">Arrivée estimée</span>
      </div>
      <span className="text-sm font-bold text-[#FACC15]">{eta || "…"}</span>
    </div>
  );
}

function TrackingMap({ departureCoords, destinationCoords, driverPos, departure, destination }) {
  const map = useMap();
  const routesLib = useMapsLibrary("routes");
  const rendererRef = useRef(null);

  // Draw the base route once
  useEffect(() => {
    if (!map || !routesLib || !departureCoords || !destinationCoords) return;
    if (rendererRef.current) return;
    const svc = new routesLib.DirectionsService();
    rendererRef.current = new routesLib.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: { strokeColor: "#0A2540", strokeWeight: 4, strokeOpacity: 0.55 },
    });
    svc.route(
      {
        origin: { lat: departureCoords.lat, lng: departureCoords.lng },
        destination: { lat: destinationCoords.lat, lng: destinationCoords.lng },
        travelMode: routesLib.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && rendererRef.current) rendererRef.current.setDirections(result);
      }
    );
    return () => {
      if (rendererRef.current) { rendererRef.current.setMap(null); rendererRef.current = null; }
    };
  }, [map, routesLib, departureCoords, destinationCoords]);

  // Pan / fit whenever driver position updates. Priority:
  //   1. If both driver + pickup coords known → fit bounds so the follower
  //      always sees "where the car is" AND "where the passenger was".
  //   2. Otherwise pan smoothly to the driver.
  useEffect(() => {
    if (!map || !driverPos?.lat || !driverPos?.lng) return;
    if (departureCoords?.lat) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: driverPos.lat, lng: driverPos.lng });
      bounds.extend({ lat: departureCoords.lat, lng: departureCoords.lng });
      if (destinationCoords?.lat) bounds.extend({ lat: destinationCoords.lat, lng: destinationCoords.lng });
      map.fitBounds(bounds, 80);
    } else {
      map.panTo({ lat: driverPos.lat, lng: driverPos.lng });
    }
  }, [map, driverPos?.lat, driverPos?.lng, departureCoords?.lat, departureCoords?.lng, destinationCoords?.lat, destinationCoords?.lng]);

  // Initial map center: always the passenger's pickup point (that's what the
  // relative wants to see first). Fallbacks preserve the previous behavior
  // for legacy bookings that didn't capture pickup GPS.
  const center = departureCoords || destinationCoords || (driverPos?.lat ? { lat: driverPos.lat, lng: driverPos.lng } : { lat: 3.848, lng: 11.5021 });

  const departureIcon = useMemo(() => ({
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" fill="#10B981" stroke="white" stroke-width="3"/></svg>'),
    scaledSize: { width: 24, height: 24 },
  }), []);
  const destinationIcon = useMemo(() => ({
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" fill="#EF4444" stroke="white" stroke-width="3"/></svg>'),
    scaledSize: { width: 24, height: 24 },
  }), []);
  const carIcon = useMemo(() => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
      <circle cx="23" cy="23" r="22" fill="rgba(250,204,21,0.20)"/>
      <circle cx="23" cy="23" r="15" fill="rgba(250,204,21,0.40)"/>
      <g transform="translate(23 23)">
        <path d="M -6 -9 L 6 -9 L 8 -3 L 8 8 L 6 10 L -6 10 L -8 8 L -8 -3 Z" fill="#FACC15" stroke="#0A2540" stroke-width="1.4" stroke-linejoin="round"/>
        <rect x="-5" y="-7.5" width="10" height="5.5" rx="1.2" fill="#0A2540" opacity="0.85"/>
        <rect x="-5" y="0.5" width="10" height="4" rx="1" fill="#0A2540" opacity="0.55"/>
      </g>
    </svg>`;
    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: { width: 46, height: 46 },
      anchor: { x: 23, y: 23 },
    };
  }, []);

  return (
    <Map
      defaultCenter={center}
      defaultZoom={13}
      mapId="trigotour-public-tracking"
      style={{ width: "100%", height: "100%" }}
      gestureHandling="greedy"
      disableDefaultUI={false}
      zoomControl={true}
      mapTypeControl={false}
      streetViewControl={false}
    >
      {departureCoords && <Marker position={departureCoords} title={departure || "Départ"} icon={departureIcon} />}
      {destinationCoords && <Marker position={destinationCoords} title={destination || "Destination"} icon={destinationIcon} />}
      {driverPos?.lat && driverPos?.lng && <Marker position={{ lat: driverPos.lat, lng: driverPos.lng }} title="Véhicule" icon={carIcon} />}
    </Map>
  );
}
